import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy,
  Download,
  ExternalLink,
  Info,
  Loader2,
} from "lucide-react";
import {
  runDashboardChart,
  useDashboardChart,
  useDashboards,
} from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ImpersonationReasonPicker } from "@/components/shared/impersonation-reason-picker";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MaskedText } from "@/components/masked-text";
import { useAuth } from "@/lib/auth";
import { toCsv, downloadCsv, type CsvValue } from "@/lib/utils/csv";
import {
  DashboardTable,
  filterTableRows,
  sortTableRows,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "leads";

/**
 * Row caps, mirroring `DEFAULT_LEADS_LIMIT` / `MAX_LEADS_LIMIT` in
 * `backend/src/modules/dashboards/definitions/_leads-columns.ts`.
 *
 * The table deliberately renders fewer rows than the export carries: 20 000
 * rows of a 31-column table is a dead page, but a truncated CSV is a marketing
 * list quietly missing people. So the screen shows the newest `TABLE_LIMIT` and
 * says so, and the download re-runs the segment at `EXPORT_LIMIT`.
 */
const TABLE_LIMIT = 2000;
const EXPORT_LIMIT = 20000;

/**
 * Segment ids must match the chart ids in
 * `backend/src/modules/dashboards/definitions/leads.ts`. Every segment returns
 * the same column contract, which is what lets one table + one export serve
 * all of them.
 */
const SEGMENT_GROUPS = [
  {
    label: "1. Registrering",
    segments: [
      { id: "email-no-bankid", label: "Registrert e-post, uten BankID" },
      { id: "bankid-no-purchase", label: "Opprettet bruker med BankID, ikke betalt" },
    ],
  },
  {
    label: "2. Helseengasjement",
    segments: [
      { id: "survey-answered-no-purchase", label: "Svart på spørsmål, ikke betalt" },
      { id: "survey-last-answer-no-purchase", label: "Svart på spørsmål (sist svar)" },
      { id: "survey-started-not-finished", label: "Påbegynt undersøkelse" },
      { id: "survey-completed-no-purchase", label: "Fullført undersøkelse, ikke betalt" },
      { id: "wearable-no-purchase", label: "Wearable, ikke betalt" },
    ],
  },
  {
    label: "3. Kjøpsintensjon",
    segments: [
      { id: "tier-selected-no-purchase", label: "Valgte abonnement, ikke betalt" },
      { id: "abandoned-checkout", label: "Avbrutt betaling" },
    ],
  },
  {
    label: "Kunder · oppsalg og reaktivering",
    segments: [
      { id: "b2b-no-personal-purchase", label: "B2B uten eget kjøp" },
      { id: "churned-subscribers", label: "Churnede abonnenter" },
    ],
  },
] as const;
const SEGMENTS = SEGMENT_GROUPS.flatMap((group) => [...group.segments]);

/**
 * Numeric fields are typed `number | string` because postgres returns bigint
 * and numeric as strings over the wire — same convention as the other
 * dashboards. Always run them through `Number()` before arithmetic.
 */
type LeadRow = {
  email: string | null;
  name: string | null;
  auth_user_id: string | null;
  signed_up_at: string | null;
  profile_created_at: string | null;
  activity_at: string | null;
  days_since_signup: number | string | null;
  gender: string;
  age: number | string | null;
  marketing_consent: boolean | null;
  analytics_consent: boolean | null;
  health_survey_completed: boolean | null;
  answer_count: number | string;
  first_answer_at: string | null;
  last_answer_at: string | null;
  wearable_sources: string;
  wearable_days: number | string;
  first_wearable_at: string | null;
  last_wearable_at: string | null;
  personal_visit_count: number | string;
  company_visit_count: number | string;
  self_reported_visit_count: number | string;
  last_personal_visit_at: string | null;
  last_company_visit_at: string | null;
  last_self_reported_visit_at: string | null;
  ever_paid_sub: boolean;
  sub_status: string;
  sub_tier: string;
  first_paid_sub_at: string | null;
  churned_at: string | null;
  channel: string;
  company_name: string;
  signup_company_slug: string;
  /**
   * How many users matched before the row cap — the same on every row. Lets
   * the page tell "this segment has 340 people" from "this segment was cut off
   * at 2000", which otherwise look identical from the outside.
   */
  total_matches: number | string;
};

/** Who the reason dialog is about, while it waits for an answer. */
type AnamnesisTarget = { authUserId: string; label: string };

type ExclusionRow = {
  engaged: number | string;
  leads: number | string;
  excluded_personal: number | string;
  excluded_company: number | string;
  excluded_other: number | string;
  /** Only when true do the payer counts explain *why* someone was dropped. */
  by_payer: boolean;
  /** The reason the qualification drops someone, when it isn't about payment. */
  exclusion_reason: string;
};

/**
 * Column order is shared by the table and the CSV, so they can't drift.
 * `filterable` marks the columns with a closed set of values — the table
 * gets a column filter for each of them. "Mark.samtykke" is left out: the
 * "Kun markedsføringssamtykke" chip in the filter card already covers it.
 */
const COLUMNS: {
  key: keyof LeadRow;
  header: string;
  numeric?: boolean;
  filterable?: boolean;
  formatOption?: (value: string) => string;
}[] = [
  { key: "email", header: "E-post" },
  { key: "name", header: "Navn" },
  { key: "signed_up_at", header: "Registrert" },
  { key: "activity_at", header: "Aktivitet" },
  { key: "days_since_signup", header: "Dager siden reg.", numeric: true },
  { key: "channel", header: "Kanal", filterable: true },
  {
    key: "gender",
    header: "Kjønn",
    filterable: true,
    formatOption: (v) => (v === "male" ? "Mann" : v === "female" ? "Kvinne" : v),
  },
  { key: "age", header: "Alder", numeric: true },
  { key: "marketing_consent", header: "Mark.samtykke" },
  {
    key: "health_survey_completed",
    header: "Undersøkelse fullført",
    filterable: true,
    formatOption: (v) => (v === "true" ? "Ja" : "Nei"),
  },
  { key: "answer_count", header: "Antall svar", numeric: true },
  { key: "first_answer_at", header: "Første svar" },
  { key: "last_answer_at", header: "Siste svar" },
  { key: "wearable_sources", header: "Wearable-kilder" },
  { key: "wearable_days", header: "Wearable-dager", numeric: true },
  { key: "first_wearable_at", header: "Første synk" },
  { key: "last_wearable_at", header: "Siste synk" },
  { key: "personal_visit_count", header: "Egenbetalte besøk", numeric: true },
  { key: "company_visit_count", header: "Bedriftsbetalte besøk", numeric: true },
  { key: "self_reported_visit_count", header: "Selvrapporterte", numeric: true },
  { key: "last_personal_visit_at", header: "Siste egenbetalte" },
  { key: "last_company_visit_at", header: "Siste bedriftsbetalte" },
  { key: "company_name", header: "Bedrift (betaler)" },
  {
    key: "ever_paid_sub",
    header: "Har betalt abo.",
    filterable: true,
    formatOption: (v) => (v === "true" ? "Ja" : "Nei"),
  },
  { key: "sub_status", header: "Abo.status", filterable: true },
  { key: "sub_tier", header: "Abo.type", filterable: true },
  { key: "first_paid_sub_at", header: "Første abo." },
  { key: "churned_at", header: "Churnet" },
  { key: "signup_company_slug", header: "Bedrift (registrering)" },
  { key: "profile_created_at", header: "Profil opprettet" },
  { key: "auth_user_id", header: "Bruker-ID" },
];

/** The same columns as a table definition — the table and the CSV share COLUMNS. */
const TABLE_COLUMNS: DashboardTableColumn<LeadRow>[] = COLUMNS.map((c) => ({
  id: c.key,
  label: c.header,
  value: (row) => row[c.key],
  numeric: c.numeric,
  align: c.numeric ? "right" : undefined,
  filterable: c.filterable,
  formatOption: c.formatOption,
}));

/**
 * Booleans render as Ja/Nei in both the table and the export, so the CSV reads
 * the same way the screen did. Blanks differ on purpose: an em dash is a
 * display affordance, and writing it into a data column would turn every empty
 * date into a junk value the moment someone sorts or filters on it.
 */
function csvCell(row: LeadRow, key: keyof LeadRow): CsvValue {
  const v = row[key];
  if (typeof v === "boolean") return v ? "Ja" : "Nei";
  return v as CsvValue;
}

function formatCell(row: LeadRow, key: keyof LeadRow): string {
  const v = row[key];
  if (typeof v === "boolean") return v ? "Ja" : "Nei";
  if (v == null || v === "") return "—";
  return String(v);
}

export function LeadsDashboard() {
  // Everything visible lives in the URL — including the segment, the table
  // sort and the column filters — so a pasted link reproduces the exact list
  // someone is looking at.
  const [
    {
      segment,
      signedUpFrom,
      signedUpTo,
      activityFrom,
      activityTo,
      consentOnly,
    },
    update,
  ] = useDashboardUrlState({
    segment: "email-no-bankid",
    signedUpFrom: "",
    signedUpTo: "",
    activityFrom: "",
    activityTo: "",
    consentOnly: "",
  });

  // Opening someone's anamnese reads their health record, so it goes through
  // the same impersonate-with-a-reason step as the anamnese list itself.
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [reasonTarget, setReasonTarget] = useState<AnamnesisTarget | null>(null);
  const [reason, setReason] = useState("");
  const [impersonating, setImpersonating] = useState(false);

  const params = useMemo(
    () => ({
      signedUpFrom: signedUpFrom || undefined,
      signedUpTo: signedUpTo || undefined,
      activityFrom: activityFrom || undefined,
      activityTo: activityTo || undefined,
      consentOnly: consentOnly === "1" || undefined,
      limit: TABLE_LIMIT,
    }),
    [signedUpFrom, signedUpTo, activityFrom, activityTo, consentOnly]
  );

  const leads = useDashboardChart<typeof params, LeadRow>(
    DASHBOARD_ID,
    segment,
    params
  );

  // Why this segment is smaller than the pool of people who did the thing.
  // Same filters as the segment query, so the two always describe one view.
  const exclusionParams = useMemo(() => ({ ...params, segment }), [params, segment]);
  const exclusions = useDashboardChart<typeof exclusionParams, ExclusionRow>(
    DASHBOARD_ID,
    "exclusions",
    exclusionParams
  );

  const segmentLabel =
    SEGMENTS.find((s) => s.id === segment)?.label ?? segment;

  const { data: dashboards } = useDashboards();
  const segmentDescription = dashboards
    ?.find((d) => d.id === DASHBOARD_ID)
    ?.charts.find((c) => c.id === segment)?.description;

  const leadsTable = useDashboardTable("leads", TABLE_COLUMNS, leads.data);
  // The KPIs below measure the table's view, so they follow the column
  // filters like the rows do.
  const rows = leadsTable.viewRows;

  // Both read the raw query rows, not the view: Treff is the size of the
  // real segment, and `truncated` says whether the *display* fetch hit its
  // row cap. Column filters can shrink or empty the view, which must not
  // change either. Every raw row carries the same total; before the first
  // fetch, fall back to what we have.
  const rawRows = leads.data ?? [];
  const total = Number(rawRows[0]?.total_matches ?? rawRows.length);
  const truncated = total > rawRows.length;

  const emails = useMemo(
    () => rows.map((r) => r.email).filter((e): e is string => !!e),
    [rows]
  );
  const consentCount = useMemo(
    () => rows.filter((r) => r.marketing_consent).length,
    [rows]
  );
  const wearableCount = useMemo(
    () => rows.filter((r) => Number(r.wearable_days) > 0).length,
    [rows]
  );

  const openAnamnesis = (row: LeadRow) => {
    if (!row.auth_user_id) return;
    setReason("");
    setReasonTarget({
      authUserId: row.auth_user_id,
      label: row.name || row.email || row.auth_user_id,
    });
  };

  const confirmAnamnesis = async (selectedReason: string) => {
    const reason = selectedReason.trim();
    if (!reasonTarget || impersonating || !reason) return;
    const { authUserId } = reasonTarget;
    setReasonTarget(null);
    setImpersonating(true);
    try {
      await impersonate(authUserId, reason);
      navigate(`/anamnesis/${authUserId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunne ikke impersonere bruker");
    } finally {
      setImpersonating(false);
    }
  };

  // The table is capped for rendering, so exporting what's on screen would
  // silently drop everyone past the cap. When that's the case, re-run the
  // segment at the export cap first. A mutation rather than a query because
  // this is a one-off action, not state the page is displaying.
  const exportRun = useMutation({
    mutationFn: () =>
      runDashboardChart<LeadRow>(DASHBOARD_ID, segment, {
        ...params,
        limit: EXPORT_LIMIT,
      }),
  });

  const handleDownload = async () => {
    let raw: LeadRow[] = leads.data ?? [];
    if (truncated) {
      try {
        raw = await exportRun.mutateAsync();
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Kunne ikke hente hele listen"
        );
        return;
      }
    }
    // The export carries the table's view — column filters first, then the
    // sort — over the full (uncapped-for-display) rows.
    const sortColumn =
      TABLE_COLUMNS.find((c) => c.id === leadsTable.sort) ?? null;
    const full = sortTableRows(
      filterTableRows(raw, TABLE_COLUMNS, leadsTable.filters),
      sortColumn,
      leadsTable.dir,
    );

    downloadCsv(
      toCsv(
        COLUMNS.map((c) => c.header),
        full.map((row) => COLUMNS.map((c) => csvCell(row, c.key))),
        ",",
        // Email-only registrations are unauthenticated input. This is a human-
        // facing spreadsheet export, so never let a cell execute as a formula.
        { neutralizeFormulas: true }
      ),
      `leads-${segment}-${new Date().toISOString().slice(0, 10)}.csv`,
      // Names carry æ/ø/å and this file gets opened in Excel, not fed to a parser.
      { bom: true }
    );

    // Even the export cap can bite. When the raw fetch hit it, the file may
    // be missing matches — with column filters active we can't count how
    // many, since the segment total is measured before the filters.
    if (raw.length >= EXPORT_LIMIT) {
      toast.warning(
        leadsTable.hasActiveFilters
          ? `Segmentet er begrenset til ${nb(EXPORT_LIMIT)} rader, så eksporten kan mangle treff som matcher kolonnephiltrene. Snevre inn filtrene for å få resten.`
          : `Eksporten er begrenset til ${nb(EXPORT_LIMIT)} rader — ${nb(
              total - full.length,
            )} treff ble ikke med. Snevre inn filtrene for å få resten.`
      );
    }
  };

  const handleCopyEmails = async () => {
    if (emails.length === 0) {
      toast.error("Ingen e-postadresser å kopiere");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails.join("\n"));
      toast.success(`Kopierte ${emails.length} e-postadresser`);
    } catch {
      toast.error("Kunne ikke kopiere til utklippstavlen");
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <p className="text-xs text-admin-text-subtle">
          Fra registrering til kjøp. Segmentene kan overlappe, og brukere kan hoppe over steg.
          «Påbegynt undersøkelse» inkluderer også betalende brukere.
        </p>
        <div className="grid gap-4 xl:grid-cols-2">
          {SEGMENT_GROUPS.map((group) => (
            <fieldset key={group.label} className="min-w-0 space-y-2">
              <legend className="text-xs font-medium text-admin-text-secondary">
                {group.label}
              </legend>
              <div className="flex flex-wrap gap-2">
                {group.segments.map((s) => (
                  <Button
                    key={s.id}
                    variant={segment === s.id ? "default" : "outline"}
                    size="sm"
                    aria-pressed={segment === s.id}
                    onClick={() => update({ segment: s.id })}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        {segmentDescription && (
          <p className="text-xs text-admin-text-subtle max-w-4xl">
            {segmentDescription}
          </p>
        )}
      </div>

      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Registrert fra</span>
            <Input
              type="date"
              value={signedUpFrom}
              onChange={(e) => update({ signedUpFrom: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Registrert til</span>
            <Input
              type="date"
              value={signedUpTo}
              onChange={(e) => update({ signedUpTo: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Aktivitet fra</span>
            <Input
              type="date"
              value={activityFrom}
              onChange={(e) => update({ activityFrom: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Aktivitet til</span>
            <Input
              type="date"
              value={activityTo}
              onChange={(e) => update({ activityTo: e.target.value })}
              className="w-44"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={consentOnly === "1" ? "default" : "outline"}
            size="sm"
            onClick={() => update({ consentOnly: consentOnly === "1" ? "" : "1" })}
          >
            Kun markedsføringssamtykke
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              update({
                signedUpFrom: "",
                signedUpTo: "",
                activityFrom: "",
                activityTo: "",
                consentOnly: "",
              })
            }
          >
            Nullstill filtre
          </Button>
        </div>
      </FiltersCard>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Treff is the whole segment; the rest are measured on the rows in
            the table — column filters included — and say so when the table
            holds only a sample of the segment. */}
        <KpiCard label="Treff" value={nb(total)} />
        <KpiCard label={sampleLabel("Med e-post", truncated)} value={nb(emails.length)} />
        <KpiCard
          label={sampleLabel("Med mark.samtykke", truncated)}
          value={`${nb(consentCount)} (${pct(consentCount, rows.length)})`}
        />
        <KpiCard
          label={sampleLabel("Med wearable", truncated)}
          value={`${nb(wearableCount)} (${pct(wearableCount, rows.length)})`}
        />
      </div>

      {truncated && (
        <TruncationNote shown={rawRows.length} total={total} />
      )}

      <ExclusionNote
        row={exclusions.data?.[0]}
        onShowB2b={() => update({ segment: "b2b-no-personal-purchase" })}
        showB2bLink={segment !== "b2b-no-personal-purchase"}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle>{segmentLabel}</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyEmails}
              disabled={emails.length === 0}
            >
              <Copy className="h-3.5 w-3.5" />
              Kopier e-poster ({emails.length})
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDownload}
              disabled={rows.length === 0 || exportRun.isPending}
            >
              {exportRun.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              {/* Counts the file, not the table — the export fetches past the
                  display cap, so promising `rows.length` would understate it. */}
              Last ned CSV ({nb(Math.min(total, EXPORT_LIMIT))})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {leads.isLoading ? (
            <ChartSkeleton />
          ) : leads.isError ? (
            <ErrorBlock message={leads.error?.message} />
          ) : (leads.data?.length ?? 0) === 0 ? (
            <EmptyBlock>Ingen brukere i dette segmentet.</EmptyBlock>
          ) : (
            <DashboardTable
              table={leadsTable}
              columns={TABLE_COLUMNS}
              renderRow={(row) => (
                <TableRow key={row.auth_user_id ?? row.email}>
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      className={
                        c.numeric
                          ? "text-right tabular-nums whitespace-nowrap"
                          : "whitespace-nowrap"
                      }
                    >
                      {c.key === "email" && row.email && row.auth_user_id ? (
                        <button
                          type="button"
                          onClick={() => openAnamnesis(row)}
                          disabled={impersonating}
                          title="Åpne anamnese"
                          className="inline-flex items-center gap-1 text-admin-primary hover:underline disabled:opacity-50"
                        >
                          <MaskedText value={row.email} type="email" />
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : c.key === "email" && row.email ? (
                        <MaskedText value={row.email} type="email" />
                      ) : c.key === "name" && row.name ? (
                        <MaskedText value={row.name} type="name" />
                      ) : (
                        formatCell(row, c.key)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Impersonation reason dialog */}
      <Dialog
        open={!!reasonTarget}
        onOpenChange={(open) => {
          if (!open) setReasonTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grunn for impersonering</DialogTitle>
          </DialogHeader>
          {reasonTarget && (
            <div className="space-y-3">
              <p className="text-sm text-admin-text-secondary">
                Åpner anamnesen til{" "}
                <span className="font-medium text-admin-text-primary">
                  <MaskedText
                    value={reasonTarget.label}
                    type={reasonTarget.label.includes("@") ? "email" : "name"}
                  />
                </span>
              </p>
              <ImpersonationReasonPicker
                value={reason}
                onChange={setReason}
                onConfirm={confirmAnamnesis}
                disabled={impersonating}
                confirmLabel="Bekreft og åpne anamnese"
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setReasonTarget(null)}>
                  Avbryt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The row cap is invisible from the inside: a segment cut off at 2000 renders
 * exactly like one that really has 2000 people, and the CSV would have gone out
 * quietly missing everyone else. This says which of the two you're looking at.
 */
function TruncationNote({ shown, total }: { shown: number; total: number }) {
  return (
    <div className="rounded-md border border-admin-primary/40 bg-admin-primary/5 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Info className="h-4 w-4 shrink-0 text-admin-primary" />
        <span className="text-admin-text-secondary">
          Tabellen viser de{" "}
          <span className="tabular-nums text-admin-text-primary">{nb(shown)}</span>{" "}
          sist registrerte av{" "}
          <span className="tabular-nums text-admin-text-primary">{nb(total)}</span>{" "}
          treff.{" "}
          {total > EXPORT_LIMIT
            ? `CSV-nedlastingen tar med de første ${nb(EXPORT_LIMIT)} — snevre inn filtrene for å få resten.`
            : "CSV-nedlastingen tar med alle."}
        </span>
      </div>
    </div>
  );
}

/**
 * Marks a KPI that's measured on the loaded rows rather than the whole segment,
 * so a percentage of the first 2000 can't be read as a percentage of all of it.
 */
function sampleLabel(label: string, truncated: boolean): string {
  return truncated ? `${label} (av viste)` : label;
}

/**
 * The exclusion is the least visible part of a lead list — you see the
 * survivors, never the filter. "3 treff" reads very differently once you know
 * five more people did the same thing and were dropped for already being
 * customers, so this spells out the drop and what caused it.
 *
 * Silent when nothing was excluded: a segment that filters nobody shouldn't
 * grow a banner saying so.
 */
function ExclusionNote({
  row,
  onShowB2b,
  showB2bLink,
}: {
  row: ExclusionRow | undefined;
  onShowB2b: () => void;
  showB2bLink: boolean;
}) {
  if (!row) return null;
  const engaged = Number(row.engaged);
  const leads = Number(row.leads);
  const personal = Number(row.excluded_personal);
  const company = Number(row.excluded_company);
  const other = Number(row.excluded_other);
  const total = personal + company + other;
  if (total === 0) return null;

  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
        <Info className="h-4 w-4 shrink-0 text-admin-info" />
        <span className="text-admin-text-secondary">
          <span className="tabular-nums text-admin-text-primary">{engaged}</span>{" "}
          gjorde dette —{" "}
          <span className="tabular-nums text-admin-text-primary">{leads}</span> er
          i listen,{" "}
          <span className="tabular-nums text-admin-text-primary">{total}</span>{" "}
          filtrert bort:
        </span>
        {row.by_payer ? (
          <>
            {personal > 0 && <Pill>{personal} har betalt selv</Pill>}
            {company > 0 &&
              (showB2bLink ? (
                <button
                  type="button"
                  onClick={onShowB2b}
                  className="rounded-full border border-admin-primary/40 bg-admin-primary/10 px-2.5 py-0.5 text-xs text-admin-primary hover:bg-admin-primary/20"
                  title="Vis disse i B2B-segmentet"
                >
                  {company} betalt av arbeidsgiver (B2B) →
                </button>
              ) : (
                <Pill>{company} betalt av arbeidsgiver (B2B)</Pill>
              ))}
            {other > 0 && <Pill>{other} av annen grunn</Pill>}
          </>
        ) : (
          // This segment doesn't filter on payment, so the payer counts would
          // describe a coincidence rather than a cause.
          <Pill>
            {total} {row.exclusion_reason}
          </Pill>
        )}
      </div>
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-admin-card-border bg-admin-slate px-2.5 py-0.5 text-xs text-admin-text-secondary">
      {children}
    </span>
  );
}

/** Norwegian thousands separators, so 12345 reads as 12 345. */
function nb(n: number): string {
  return n.toLocaleString("no");
}

function pct(part: number, total: number): string {
  if (total === 0) return "0 %";
  return `${Math.round((part / total) * 100)} %`;
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-admin-text-subtle">{label}</p>
        <p className="text-2xl font-semibold text-admin-text-primary tabular-nums">
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">{children}</p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
